import difference from '@turf/difference';
import union from '@turf/union';
import {
  polygon as turfPolygon,
  type Feature,
  type Polygon,
  type MultiPolygon,
} from '@turf/helpers';

/**
 * The unit's setback keep-out ring: the inner drainable region's outer ring(s),
 * to be rendered as holes in the unit polygon so the band between them shades.
 *
 * Ported from the mock (`data/developmentAreaGeometry.ts` `buildSetbackRings`),
 * converted to GeoJSON `[lng, lat]` and turf v6. Each unit edge grows a strip
 * inward by an anisotropic distance — an edge parallel to the unit azimuth takes
 * the full Cross-line setback, an end edge takes the In-line setback, diagonals
 * the elliptical blend — and the inner region = unit − union(strips).
 */

const FT_PER_METER = 3.28084;
const FT_PER_DEG_LAT = 111_320 * FT_PER_METER;

type PolyFeature = Feature<Polygon | MultiPolygon>;

// Round to ~1e-9 deg so turf's polyclip sees stable, deduped vertices.
const snap = (v: number): number => Math.round(v * 1e9) / 1e9;

// Drop a duplicated closing vertex so no edge normal comes off a zero segment.
const openRing = (ring: GeoJSON.Position[]): GeoJSON.Position[] => {
  if (
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  ) {
    return ring.slice(0, -1);
  }
  return ring.slice();
};

// Close a ring (first === last) for turf.
const closeRing = (ring: GeoJSON.Position[]): GeoJSON.Position[] => {
  if (ring.length === 0) return ring;
  const last = ring[ring.length - 1];
  if (ring[0][0] !== last[0] || ring[0][1] !== last[1])
    return [...ring, ring[0]];
  return ring;
};

/**
 * Inner drainable ring(s) for a unit under the two directional setbacks.
 *
 * @param {Array} outerRing - Unit outer ring, `[lng, lat]` positions.
 * @param {number} azimuthRad - Unit azimuth in radians (long-axis direction).
 * @param {number} inlineFt - In-line setback (ft), taken off the unit ends.
 * @param {number} crosslineFt - Cross-line setback (ft), off the long sides.
 * @return {Array} Inner ring(s) as `[lng, lat]` position arrays, or null.
 */
export const unitSetbackInnerRings = (
  outerRing: GeoJSON.Position[],
  azimuthRad: number,
  inlineFt: number,
  crosslineFt: number
): GeoJSON.Position[][] | null => {
  const inline = Number.isFinite(inlineFt) && inlineFt > 0 ? inlineFt : 0;
  const crossline =
    Number.isFinite(crosslineFt) && crosslineFt > 0 ? crosslineFt : 0;
  if (inline <= 0 && crossline <= 0) return null;

  const ring = openRing(outerRing);
  if (ring.length < 3) return null;

  // Planar feet frame anchored at the centroid — x east, y north.
  let sumLng = 0;
  let sumLat = 0;
  for (const [lng, lat] of ring) {
    sumLng += lng;
    sumLat += lat;
  }
  const cLng = sumLng / ring.length;
  const cLat = sumLat / ring.length;
  const ftLng = FT_PER_DEG_LAT * Math.cos((cLat * Math.PI) / 180);
  const toLngLat = (x: number, y: number): GeoJSON.Position => [
    snap(cLng + x / ftLng),
    snap(cLat + y / FT_PER_DEG_LAT),
  ];

  const cosA = Math.cos(azimuthRad);
  const sinA = Math.sin(azimuthRad);

  const verts = ring.map<[number, number]>(([lng, lat]) => [
    (lng - cLng) * ftLng,
    (lat - cLat) * FT_PER_DEG_LAT,
  ]);

  const strips: PolyFeature[] = [];

  // The keep-out region is the boundary grown by the setback ELLIPSE — semi-axis
  // `inline` along the unit azimuth, `crossline` across it. Growing a segment by
  // that ellipse gives a rectangle flush with the segment (no overhang) plus the
  // ellipse at each end, which is what rounds the corners off.
  //
  // The rectangles used to be extended past both ends by the setback distance so
  // neighbouring ones would square up at corners. That works while every edge is
  // far longer than the setback, but a SHORT edge — the step left where a unit
  // and the survey area beside it end at slightly different places — then threw
  // a 330 ft strip out along its own normal, biting a wedge out of the band that
  // no edge of the unit justified. The vertex ellipses fill those corners without
  // reaching past the edges they belong to.
  for (let i = 0; i < verts.length; i += 1) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len < 1) continue;
    const ex = (b[0] - a[0]) / len;
    const ey = (b[1] - a[1]) / len;
    const nx = -ey;
    const ny = ex;
    // Support distance of the setback ellipse along this edge's normal.
    const d = Math.hypot(
      inline * (nx * cosA + ny * sinA),
      crossline * (-nx * sinA + ny * cosA)
    );
    if (d <= 0) continue;
    const corner = toLngLat(a[0] + nx * d, a[1] + ny * d);
    strips.push(
      turfPolygon([
        [
          corner,
          toLngLat(b[0] + nx * d, b[1] + ny * d),
          toLngLat(b[0] - nx * d, b[1] - ny * d),
          toLngLat(a[0] - nx * d, a[1] - ny * d),
          corner,
        ],
      ]) as PolyFeature
    );
  }

  // One rectangle per vertex to square the corners off.
  //
  // The exact keep-out is the boundary grown by the setback ELLIPSE, which would
  // put a rounded cap on every corner. Setbacks are surveyed as straight offsets,
  // so the corner is filled with the ellipse's circumscribing rectangle instead —
  // `inline` along the unit azimuth, `crossline` across it, the same reach as the
  // two strips that meet there. On a rectangular unit that is exactly a square
  // corner; elsewhere it covers marginally more, always with straight edges.
  for (const [vx, vy] of verts) {
    const alongX = inline * cosA;
    const alongY = inline * sinA;
    const acrossX = -crossline * sinA;
    const acrossY = crossline * cosA;
    const corner = toLngLat(vx + alongX + acrossX, vy + alongY + acrossY);
    strips.push(
      turfPolygon([
        [
          corner,
          toLngLat(vx + alongX - acrossX, vy + alongY - acrossY),
          toLngLat(vx - alongX - acrossX, vy - alongY - acrossY),
          toLngLat(vx - alongX + acrossX, vy - alongY + acrossY),
          corner,
        ],
      ]) as PolyFeature
    );
  }

  if (strips.length === 0) return null;

  const unitFeature = turfPolygon([
    closeRing(ring.map(([lng, lat]) => [snap(lng), snap(lat)])),
  ]) as PolyFeature;

  let inner: PolyFeature | null;
  try {
    let band: PolyFeature | null = strips[0];
    for (let i = 1; i < strips.length; i += 1) {
      band = band ? (union(band, strips[i]) as PolyFeature | null) : strips[i];
    }
    inner = band
      ? (difference(unitFeature, band) as PolyFeature | null)
      : unitFeature;
  } catch {
    // Unioning many overlapping strips is where polyclip is most brittle;
    // subtracting them one at a time keeps every operand a simple polygon.
    try {
      inner = unitFeature;
      for (const strip of strips) {
        inner = difference(inner, strip) as PolyFeature | null;
        if (!inner) return null;
      }
    } catch {
      return null;
    }
  }
  if (!inner) return null;

  const geom = inner.geometry;
  const polys: GeoJSON.Position[][][] =
    geom.type === 'MultiPolygon'
      ? (geom.coordinates as GeoJSON.Position[][][])
      : [geom.coordinates as GeoJSON.Position[][]];
  // Only the outer ring of each surviving piece.
  const rings = polys
    .map((poly) => poly[0])
    .filter((r): r is GeoJSON.Position[] => !!r && r.length >= 3);
  return rings.length > 0 ? rings : null;
};
